<template>
  <v-dialog v-model="visible" max-width="640" persistent scrollable @keydown.esc="close">
    <v-card>
      <!-- Header con indicador de pasos -->
      <v-card-title class="primary d-flex align-center justify-space-between py-3 px-4">
        <span class="text-h6 font-weight-bold white--text">Declaración jurada</span>
        <div class="d-flex align-center">
          <div
            v-for="i in 2"
            :key="i"
            class="step-indicator d-flex align-center justify-center ml-1"
            :class="i === step ? 'step-active' : 'step-inactive'"
          >
            <span class="step-number">{{ i }}</span>
          </div>
        </div>
      </v-card-title>

      <v-card-text class="pt-6 pb-2">
        <!-- Paso 1: Información de la atención -->
        <div v-if="step === 1">
          <p class="subtitle-1 font-weight-bold primary--text mb-4">Información de la atención</p>

          <p class="body-2 font-weight-medium grey--text text--darken-3 mb-3">Fechas relevantes:</p>

          <p class="caption font-weight-bold primary--text mb-1">Inicio de los síntomas</p>
          <v-text-field
            v-model="symptomsDate"
            type="date"
            outlined
            dense
            :max="today"
            :error="formSubmitted && !symptomsDate"
          ></v-text-field>

          <p class="caption font-weight-bold primary--text mb-1">Atención en clínica</p>
          <v-text-field
            v-model="clinicDate"
            type="date"
            outlined
            dense
            :max="today"
            :error="formSubmitted && !clinicDate"
          ></v-text-field>

          <p class="body-2 font-weight-medium grey--text text--darken-3 mb-3 mt-2">Datos de la clínica:</p>

          <p class="caption font-weight-bold primary--text mb-1">Nombre de la clínica</p>
          <v-text-field
            v-model="clinicName"
            outlined
            dense
            placeholder="Ej. Veterinaria PROVET"
            :error="formSubmitted && !clinicName"
          ></v-text-field>

          <p class="caption font-weight-bold primary--text mb-1">Correo de la clínica</p>
          <v-text-field
            v-model="clinicEmail"
            type="email"
            outlined
            dense
            placeholder="Ej. clinica@veterinaria.com"
            :rules="[v => !v || /.+@.+\..+/.test(v) || 'Ingresa un correo electrónico válido']"
            :error="formSubmitted && (!clinicEmail || !isEmailValid)"
          ></v-text-field>

          <p class="caption font-weight-bold primary--text mb-1">Teléfono de la clínica</p>
          <v-text-field
            v-model="clinicPhone"
            outlined
            dense
            :placeholder="isoCountry === 'PE' ? 'Ej. 987654321' : 'Ej. 9 2700 3674'"
            :rules="[v => !v || /^[\d\s+\-()]{7,15}$/.test(v) || 'Solo se permiten números y caracteres de teléfono']"
            :error="formSubmitted && (!clinicPhone || !isPhoneValid)"
          ></v-text-field>

          <p class="body-2 font-weight-medium grey--text text--darken-3 mb-3 mt-2">Datos del veterinario:</p>

          <p class="caption font-weight-bold primary--text mb-1">Nombre del profesional</p>
          <v-text-field
            v-model="vetName"
            outlined
            dense
            placeholder="Ej. Camila Gutierrez Núñez"
            :error="formSubmitted && !vetName"
          ></v-text-field>

          <p class="caption font-weight-bold primary--text mb-1">
            {{ isoCountry === 'PE' ? 'DNI del profesional' : 'RUT del profesional' }}
          </p>
          <v-text-field
            v-model="vetIdentification"
            outlined
            dense
            :placeholder="isoCountry === 'PE' ? 'Ej. 12345678' : 'Ej. 23.040.964-2'"
            :rules="isoCountry === 'PE'
              ? [v => !v || /^\d{8}$/.test(v) || 'El DNI debe tener exactamente 8 dígitos']
              : [v => !v || /^[\d.]{1,11}-[\dkK]$/.test(v) || 'Ingresa un RUT válido (Ej. 23.040.964-2)']"
            :error="formSubmitted && (!vetIdentification || !isVetIdValid)"
          ></v-text-field>
        </div>

        <!-- Paso 2: Detalles clínicos -->
        <div v-if="step === 2">
          <p class="subtitle-1 font-weight-bold primary--text mb-4">Detalles clínicos</p>

          <!-- Clasificación del incidente -->
          <p class="caption font-weight-bold primary--text mb-2">Clasificación del incidente:</p>
          <v-row no-gutters class="mb-1">
            <v-col cols="6" class="pr-2">
              <v-card
                outlined
                :color="incidentType === 'accidente' ? 'primary lighten-4' : 'white'"
                class="incident-card pa-3 text-center"
                style="cursor: pointer"
                @click="incidentType = 'accidente'"
              >
                <img
                  :src="incidentType === 'accidente' ? require('@/assets/accident_primary_100.svg') : require('@/assets/accident_disabled_100.svg')"
                  width="40"
                  height="40"
                />
                <p class="body-2 font-weight-medium mb-1 mt-2" :class="incidentType === 'accidente' ? 'primary--text' : 'grey--text'">Accidente</p>
                <p class="incident-subtitle" :class="incidentType === 'accidente' ? 'primary--text' : 'grey--text text--darken-1'">
                  Evento externo inesperado que causa lesiones graves
                </p>
              </v-card>
            </v-col>
            <v-col cols="6" class="pl-2">
              <v-card
                outlined
                :color="incidentType === 'urgencia' ? 'primary lighten-4' : 'white'"
                class="incident-card pa-3 text-center"
                style="cursor: pointer"
                @click="incidentType = 'urgencia'"
              >
                <img
                  src="@/assets/emergency-kit.svg"
                  width="40"
                  height="40"
                  :style="{ opacity: incidentType === 'urgencia' ? 1 : 0.35 }"
                />
                <p class="body-2 font-weight-medium mb-1 mt-2" :class="incidentType === 'urgencia' ? 'primary--text' : 'grey--text'">Urgencia</p>
                <p class="incident-subtitle" :class="incidentType === 'urgencia' ? 'primary--text' : 'grey--text text--darken-1'">
                  Riesgo vital que requiere atención inmediata
                </p>
              </v-card>
            </v-col>
          </v-row>
          <p v-if="formSubmitted && !incidentType" class="caption red--text mb-3">Obligatorio</p>

          <!-- Lapso -->
          <p class="caption font-weight-bold primary--text mb-1 mt-3">Lapso desde los síntomas hasta la atención</p>
          <v-select
            v-model="lapso"
            :items="lapsoOptions"
            outlined
            dense
            placeholder="Seleccionar"
            :error="formSubmitted && !lapso"
          ></v-select>

          <!-- Estado mascota -->
          <p class="caption font-weight-bold primary--text mb-1">Estado de la mascota al llegar a la clínica</p>
          <v-select
            v-model="estadoMascota"
            :items="estadoOptions"
            outlined
            dense
            placeholder="Seleccionar"
            :error="formSubmitted && !estadoMascota"
          ></v-select>
          <div v-if="estadoMascota === 'Otro'">
            <p class="caption font-weight-bold primary--text mb-1">Indica su estado al llegar a la clínica (otro)</p>
            <v-text-field
              v-model="estadoOtro"
              outlined
              dense
              placeholder="Ej. Atendido de urgencias"
              :error="formSubmitted && !estadoOtro"
            ></v-text-field>
          </div>

          <!-- Signos -->
          <p class="caption font-weight-bold primary--text mb-1 mt-2">Signos presentados en la mascota</p>
          <p v-if="formSubmitted && signos.length === 0" class="caption red--text mb-1">Selecciona al menos un signo</p>
          <v-checkbox
            v-for="signo in signosOptions"
            :key="`signo-${signo}`"
            v-model="signos"
            :label="signo"
            :value="signo"
            hide-details
            dense
            class="mt-1"
          ></v-checkbox>
          <div v-if="signos.includes('Otros')" class="mt-2">
            <p class="caption font-weight-bold primary--text mb-1">Indica sus signos presentados (otros)</p>
            <v-text-field
              v-model="signosOtros"
              outlined
              dense
              placeholder="Ej. Sangrado bucal"
              :error="formSubmitted && !signosOtros"
            ></v-text-field>
          </div>

          <!-- Prestaciones -->
          <p class="caption font-weight-bold primary--text mb-1 mt-4">Prestaciones realizadas</p>
          <p v-if="formSubmitted && prestaciones.length === 0" class="caption red--text mb-1">Selecciona al menos una prestación</p>
          <v-checkbox
            v-for="prest in prestacionesOptions"
            :key="`prest-${prest}`"
            v-model="prestaciones"
            :label="prest"
            :value="prest"
            hide-details
            dense
            class="mt-1"
          ></v-checkbox>
          <div v-if="prestaciones.includes('Cirugía')" class="mt-2">
            <p class="caption font-weight-bold primary--text mb-1">Indica el tipo de cirugía</p>
            <v-text-field
              v-model="cirugia"
              outlined
              dense
              placeholder="Ej. Laparotomía"
              :error="formSubmitted && prestaciones.includes('Cirugía') && !cirugia"
            ></v-text-field>
          </div>
          <div v-if="prestaciones.includes('Exámenes')" class="mt-2">
            <v-file-input
              v-model="examenesFile"
              outlined
              dense
              show-size
              label="Adjuntar resultados de exámenes"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              accept="image/jpeg,image/png,application/pdf"
            ></v-file-input>
          </div>
          <div v-if="prestaciones.includes('Otros')" class="mt-2">
            <p class="caption font-weight-bold primary--text mb-1">Indica sus prestaciones realizadas (otros)</p>
            <v-text-field
              v-model="prestacionesOtros"
              outlined
              dense
              placeholder="Ej. Rehabilitación"
            ></v-text-field>
          </div>

          <!-- Desarrollo del caso -->
          <p class="caption font-weight-bold primary--text mb-1 mt-4">Desarrollo del caso</p>
          <p class="caption grey--text text--darken-2 mb-1">
            Describe cómo ocurrió el siniestro, la evolución de los síntomas y la razón de la consulta.
          </p>
          <v-textarea
            v-model="desarrollo"
            outlined
            dense
            rows="4"
            placeholder="Ej. Ingirió cuerpo extraño el lunes, tuvo vómitos el martes y fue atendido con..."
            :error="formSubmitted && !desarrollo"
          ></v-textarea>

          <!-- Términos -->
          <p class="body-2 font-weight-medium grey--text text--darken-3 mb-2 mt-2">Aceptación de términos:</p>
          <v-checkbox
            v-model="termsAccepted"
            dense
            :error="formSubmitted && !termsAccepted"
          >
            <template v-slot:label>
              <span class="body-2">
                Declaro que la información es verdadera y autorizo a Pawer a verificarla con clínicas u otros proveedores.
              </span>
            </template>
          </v-checkbox>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="px-4 py-4">
        <v-btn text @click="close">Cancelar</v-btn>
        <v-spacer></v-spacer>
        <v-btn v-if="step === 2" text @click="step = 1">Atrás</v-btn>
        <v-btn v-if="step === 1" color="primary" depressed @click="nextStep">Continuar</v-btn>
        <v-btn v-if="step === 2" color="primary" depressed @click="submit">Guardar declaración</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'DeclaracionJuradaModal',
  props: {
    isoCountry: {
      type: String,
      default: 'CL'
    }
  },
  data() {
    return {
      visible: false,
      step: 1,
      formSubmitted: false,
      symptomsDate: null,
      clinicDate: null,
      clinicName: '',
      clinicEmail: '',
      clinicPhone: '',
      vetName: '',
      vetIdentification: '',
      incidentType: null,
      lapso: null,
      estadoMascota: null,
      estadoOtro: '',
      signos: [],
      signosOtros: '',
      prestaciones: [],
      cirugia: '',
      prestacionesOtros: '',
      examenesFile: null,
      desarrollo: '',
      termsAccepted: false,
      lapsoOptions: ['De 12 a 24 horas', 'De 24 a 36 horas', '36 horas o más'],
      estadoOptions: [
        'Crítico con compromiso vital',
        'Inestable y fue atendido con prioridad',
        'Atendido con consulta programada',
        'Otro'
      ],
      signosOptions: [
        'Convulsiones',
        'Dificultad respiratoria severa',
        'Pérdida de consciencia o desmayo',
        'Ingesta / obstrucción por cuerpo extraño',
        'Dificultad o incapacidad de orinar',
        'Otros'
      ],
      prestacionesOptions: ['Hospitalización', 'Exámenes', 'Cirugía', 'Otros']
    };
  },
  computed: {
    today() {
      return new Date().toISOString().split('T')[0];
    },
    isEmailValid() {
      return !this.clinicEmail || /.+@.+\..+/.test(this.clinicEmail);
    },
    isPhoneValid() {
      return !this.clinicPhone || /^[\d\s+\-()]{7,15}$/.test(this.clinicPhone);
    },
    isVetIdValid() {
      if (!this.vetIdentification) return true;
      return this.isoCountry === 'PE'
        ? /^\d{8}$/.test(this.vetIdentification)
        : /^[\d.]{1,11}-[\dkK]$/.test(this.vetIdentification);
    },
    isStep1Valid() {
      return !!(
        this.symptomsDate && this.clinicDate && this.clinicName &&
        this.clinicEmail && this.isEmailValid &&
        this.clinicPhone && this.isPhoneValid &&
        this.vetName && this.vetIdentification && this.isVetIdValid
      );
    },
    isStep2Valid() {
      if (!this.incidentType) return false;
      if (!this.lapso) return false;
      if (!this.estadoMascota) return false;
      if (this.estadoMascota === 'Otro' && !this.estadoOtro) return false;
      if (this.signos.length === 0) return false;
      if (this.signos.includes('Otros') && !this.signosOtros) return false;
      if (this.prestaciones.length === 0) return false;
      if (this.prestaciones.includes('Cirugía') && !this.cirugia) return false;
      if (!this.desarrollo) return false;
      if (!this.termsAccepted) return false;
      return true;
    }
  },
  methods: {
    open() {
      this.visible = true;
    },
    close() {
      this.visible = false;
    },
    nextStep() {
      this.formSubmitted = true;
      if (!this.isStep1Valid) return;
      this.formSubmitted = false;
      this.step = 2;
    },
    submit() {
      this.formSubmitted = true;
      if (!this.isStep2Valid) return;
      const formData = {
        step: 2,
        symptomsDate: this.formatDate(this.symptomsDate),
        clinicDate: this.formatDate(this.clinicDate),
        clinicName: this.clinicName,
        clinicEmail: this.clinicEmail,
        clinicPhone: this.clinicPhone,
        vetName: this.vetName,
        [this.isoCountry === 'PE' ? 'vetDni' : 'vetRut']: this.vetIdentification,
        incidentType: this.incidentType,
        lapso: this.lapso,
        estadoMascota: this.estadoMascota,
        ...(this.estadoMascota === 'Otro' && { estadoOtro: this.estadoOtro }),
        signos: this.signos,
        ...(this.signos.includes('Otros') && { signosOtros: this.signosOtros }),
        prestaciones: this.prestaciones,
        ...(this.prestaciones.includes('Cirugía') && { cirugia: this.cirugia }),
        ...(this.prestacionesOtros && { prestacionesOtros: this.prestacionesOtros }),
        desarrollo: this.desarrollo,
        termsAccepted: true,
        completed: true
      };
      this.$emit('completed', { formData, examenesFile: this.examenesFile });
      this.close();
    },
    formatDate(dateStr) {
      if (!dateStr) return null;
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    },
    resetForm() {
      this.step = 1;
      this.formSubmitted = false;
      this.symptomsDate = null;
      this.clinicDate = null;
      this.clinicName = '';
      this.clinicEmail = '';
      this.clinicPhone = '';
      this.vetName = '';
      this.vetIdentification = '';
      this.incidentType = null;
      this.lapso = null;
      this.estadoMascota = null;
      this.estadoOtro = '';
      this.signos = [];
      this.signosOtros = '';
      this.prestaciones = [];
      this.cirugia = '';
      this.prestacionesOtros = '';
      this.examenesFile = null;
      this.desarrollo = '';
      this.termsAccepted = false;
    }
  }
};
</script>

<style scoped>
.step-indicator {
  width: 28px;
  height: 28px;
  border-radius: 50%;
}

.step-active {
  background-color: white;
}

.step-inactive {
  background-color: rgba(255, 255, 255, 0.35);
}

.step-number {
  font-size: 12px;
  font-weight: 700;
  color: white;
}

.step-active .step-number {
  color: var(--v-primary-base);
}

.incident-card {
  border-radius: 12px !important;
  min-height: 130px;
}

.incident-subtitle {
  font-size: 11px;
  font-weight: 400;
  line-height: 1.4;
  margin-bottom: 0;
}
</style>
